import i18n from '../i18n';

// Validation rules derived from a head config entry.
//
// The head config is already the single description of a column — how it renders
// in the grid, how it edits in the dialog — so its rules belong here too rather
// than inside each field component, where four of them had grown private,
// inconsistent and in two cases silently destructive versions of this.

const NUMERIC_CELLS = new Set(['Amount', 'CryptoAmount', 'Number', 'Percent']);

// Cells edited by DialogJsonField or a subclass of it, which share its value
// contract: a parsed value when the text is valid JSON, the raw string when not.
const JSON_CELLS = new Set(['Json', 'ScraperSettingsJson', 'GatewaySettings', 'WalletRequisites']);

// Formats a head entry can declare via `format`, mirroring the constraints the
// cabinet API actually enforces on its request DTOs
// (x-backend: cabinet/cabinet-api/.../mapstruct/request/**/*RequestDTO.java).
// `@Email` there becomes format: 'email'; a java.util.UUID field becomes
// format: 'uuid'.
const FORMATS = {
    email: {
        // Deliberately loose: the point is to catch a typo before a round trip,
        // not to re-implement the server's own check.
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: () => i18n.t('common.invalidEmail')
    },
    uuid: {
        pattern:
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
        message: () => i18n.t('errors.invalidUuid')
    }
};

// `0` is a legitimate value, so only genuinely absent input counts as blank.
const isBlank = (value) =>
    value === '' || value === null || value === undefined;

/**
 * react-hook-form `rules` for a field.
 *
 * `field.required` was already a convention before this — but only
 * DialogUploadField and DialogTagsField read it, and only to append an asterisk.
 * Nothing enforced it, and `errors.fieldRequired` sat unused in both locales.
 */
export const buildFieldRules = (field) => {
    const rules = {};
    const validate = {};

    if (field.required) {
        rules.required = i18n.t('errors.fieldRequired');
    }

    if (NUMERIC_CELLS.has(field.cell)) {
        validate.numeric = (value) =>
            isBlank(value) ||
            Number.isFinite(Number(value)) ||
            i18n.t('errors.invalidNumber');
    }

    if (field.cell === 'Percent') {
        // The bound DialogPercentField had commented out, enforced properly.
        // Stored values are percentage numbers (15.5 means 15.5%), matching what
        // fPercent renders in the grid. 0-100 unless the head entry says otherwise:
        // a gateway's rate adjustment is signed, -50..50.
        const min = field.min ?? 0;
        const max = field.max ?? 100;
        validate.percentRange = (value) => {
            if (isBlank(value)) return true;
            const parsed = Number(value);
            return (
                (parsed >= min && parsed <= max) ||
                i18n.t('errors.percentRange', { min, max })
            );
        };
    }

    if (JSON_CELLS.has(field.cell)) {
        // DialogJsonField emits a parsed value when the text is valid JSON and the
        // raw string when it is not, the same way AmountInput reports an
        // unparseable amount. So "still a string" is exactly the signal that it
        // did not parse — which disables Save instead of posting a JSON string
        // where the API wants an object.
        validate.json = (value) =>
            isBlank(value) ||
            typeof value !== 'string' ||
            i18n.t('errors.invalidJson');
    }

    const format = FORMATS[field.format];
    if (format) {
        validate.format = (value) =>
            isBlank(value) ||
            format.pattern.test(String(value)) ||
            format.message();
    }

    // A rule only the page can state, since it reads other fields or loaded
    // lists: `validate(value, formValues)` returns true or the message. The
    // wallet's receiverInfo checks itself against its gateway's template.
    if (typeof field.validate === 'function') {
        validate.custom = (value, formValues) => field.validate(value, formValues);
    }

    if (Object.keys(validate).length) {
        rules.validate = validate;
    }

    return rules;
};
